import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  defaultEvidenceSeedDistribution,
  evidenceSeedPlanTotal,
  isActiveEvidenceSeedStatus,
  isSstv1LockerId,
  resolveEvidenceSeedPlan,
  SSTV2_EVIDENCE_DEFAULT_RESERVATION_COUNT,
  SSTV2_EVIDENCE_MAX_RESERVATION_COUNT,
  SSTV2_EVIDENCE_SEED_STATUS_LIST,
  Sstv2EvidenceSeedPlan,
  Sstv2EvidenceSeedStatus,
} from "shared/sstv2MigrationEvidenceData"

export type TenantOption = { id: string; label: string }

export type Sstv2EvidenceRunControlsProps = {
  disabled: boolean
  running: boolean
  canRun: boolean
  onRun: () => void
  cancelling: boolean
  showCancel: boolean
  onCancel: () => void

  tenantOptions: TenantOption[]
  targetTenantId: string
  onTargetTenantIdChange: (v: string) => void

  /** Operator-pasted SL1 source locker id (LOCKER-344). No default — required. */
  sourceLocker: string
  onSourceLockerChange: (v: string) => void

  reseed: boolean
  onReseedChange: (v: boolean) => void
  runRollback: boolean
  onRunRollbackChange: (v: boolean) => void

  reservationCount: number
  onReservationCountChange: (v: number) => void
  /** null = no per-status override; the total is split by the default weights. */
  distribution: Sstv2EvidenceSeedPlan | null
  onDistributionChange: (v: Sstv2EvidenceSeedPlan | null) => void
}

/**
 * Run-control panel for the Synthetic Migration tab — trigger/cancel plus the
 * knobs that shape the run: target tenant, rollback half, and how many demo
 * reservations the pre-run reseed creates.
 *
 * The reseed sizing has two modes:
 *   - a single **total**, split across statuses by the shared default weights;
 *   - an optional **per-status distribution**, which overrides the total.
 * Both preview through `resolveEvidenceSeedPlan` — the SAME helper the backend
 * re-runs on the request body — so what the operator reads here is exactly what
 * the orchestrator will execute (modulo the door clamp, which needs live locker
 * state and is reported back on the run record).
 *
 * Extracted from Sstv2SyntheticMigrationTab to keep both files under the
 * ~400-line component budget.
 */
export const Sstv2EvidenceRunControls: FC<Sstv2EvidenceRunControlsProps> = ({
  disabled,
  running,
  canRun,
  onRun,
  cancelling,
  showCancel,
  onCancel,
  tenantOptions,
  targetTenantId,
  onTargetTenantIdChange,
  sourceLocker,
  onSourceLockerChange,
  reseed,
  onReseedChange,
  runRollback,
  onRunRollbackChange,
  reservationCount,
  onReservationCountChange,
  distribution,
  onDistributionChange,
}) => {
  const { t } = useTranslation()

  // Exactly what the backend will resolve from these inputs.
  const resolvedPlan = useMemo(
    () => resolveEvidenceSeedPlan({ reservationCount, distribution }),
    [reservationCount, distribution],
  )
  const resolvedTotal = evidenceSeedPlanTotal(resolvedPlan)
  const activeTotal = useMemo(
    () =>
      SSTV2_EVIDENCE_SEED_STATUS_LIST.filter(isActiveEvidenceSeedStatus).reduce(
        (a, s) => a + resolvedPlan[s],
        0,
      ),
    [resolvedPlan],
  )

  // Shape-check with the SAME helper the backend rejects on, so the operator
  // learns the id is malformed here rather than from a 400 (LOCKER-344).
  const sourceLockerValid = isSstv1LockerId(sourceLocker)
  const sourceLockerError = sourceLocker.trim().length > 0 && !sourceLockerValid

  const setStatusCount = (s: Sstv2EvidenceSeedStatus, raw: string) => {
    const n = Math.max(0, Math.min(SSTV2_EVIDENCE_MAX_RESERVATION_COUNT, Number(raw) || 0))
    onDistributionChange({ ...(distribution ?? defaultEvidenceSeedDistribution()), [s]: n })
  }

  return (
    <div data-ui="UI-SL2W-265" style={{ display: "contents" }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <Button
          variant="contained"
          disabled={running || !canRun || !sourceLockerValid}
          onClick={onRun}
          startIcon={
            running ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {running ? t("evidenceRunInProgress") : t("runSyntheticMigration")}
        </Button>
        {showCancel && (
          <Button
            variant="outlined"
            color="error"
            disabled={cancelling}
            onClick={onCancel}
          >
            {cancelling ? t("cancelling") : t("cancelRun")}
          </Button>
        )}
        <TextField
          size="small"
          label={t("evidenceSourceLocker")}
          value={sourceLocker}
          disabled={disabled}
          onChange={(e) => onSourceLockerChange(e.target.value.trim())}
          error={sourceLockerError}
          helperText={
            sourceLockerError
              ? t("evidenceSourceLockerInvalid")
              : t("evidenceSourceLockerHint")
          }
          sx={{ minWidth: 320 }}
          inputProps={{ spellCheck: false, style: { fontFamily: "monospace" } }}
        />
        <FormControl size="small" sx={{ minWidth: 240 }} disabled={disabled}>
          <InputLabel id="evidence-target-tenant-label">
            {t("evidenceTargetTenant")}
          </InputLabel>
          <Select
            labelId="evidence-target-tenant-label"
            label={t("evidenceTargetTenant")}
            value={targetTenantId}
            onChange={(e) => onTargetTenantIdChange(e.target.value)}
          >
            {tenantOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={reseed}
              onChange={(e) => onReseedChange(e.target.checked)}
              disabled={disabled}
              size="small"
            />
          }
          label={t("evidenceReseedFirst")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={runRollback}
              onChange={(e) => onRunRollbackChange(e.target.checked)}
              disabled={disabled}
              size="small"
            />
          }
          label={t("evidenceRunRollback")}
        />
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          {t("evidenceRunDuration")}
        </Typography>
      </Stack>

      {/* Reseed sizing — only meaningful when the reseed runs. */}
      {reseed && (
        <Box sx={{ mt: 1.5 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            <TextField
              size="small"
              type="number"
              label={t("evidenceReservationCount")}
              value={reservationCount}
              disabled={disabled || !!distribution}
              onChange={(e) =>
                onReservationCountChange(
                  Math.max(
                    0,
                    Math.min(
                      SSTV2_EVIDENCE_MAX_RESERVATION_COUNT,
                      Number(e.target.value) || 0,
                    ),
                  ),
                )
              }
              inputProps={{ min: 0, max: SSTV2_EVIDENCE_MAX_RESERVATION_COUNT }}
              sx={{ width: 180 }}
              helperText={t("evidenceReservationCountHint", {
                max: SSTV2_EVIDENCE_MAX_RESERVATION_COUNT,
              })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!distribution}
                  disabled={disabled}
                  onChange={(e) =>
                    onDistributionChange(
                      e.target.checked ? defaultEvidenceSeedDistribution() : null,
                    )
                  }
                  size="small"
                />
              }
              label={t("evidenceCustomDistribution")}
            />
          </Stack>

          {distribution && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
            >
              {SSTV2_EVIDENCE_SEED_STATUS_LIST.map((s) => (
                <TextField
                  key={s}
                  size="small"
                  type="number"
                  label={s}
                  value={distribution[s]}
                  disabled={disabled}
                  onChange={(e) => setStatusCount(s, e.target.value)}
                  inputProps={{
                    min: 0,
                    max: SSTV2_EVIDENCE_MAX_RESERVATION_COUNT,
                    "aria-label": t("evidenceDistributionFieldLabel", { status: s }),
                  }}
                  sx={{ width: 120 }}
                />
              ))}
              <Button
                size="small"
                disabled={disabled}
                onClick={() => onDistributionChange(defaultEvidenceSeedDistribution())}
              >
                {t("evidenceResetDistribution")}
              </Button>
            </Stack>
          )}

          <Typography
            sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 1 }}
          >
            {t("evidenceSeedPreview", {
              total: resolvedTotal,
              active: activeTotal,
              historical: resolvedTotal - activeTotal,
              breakdown: SSTV2_EVIDENCE_SEED_STATUS_LIST.filter(
                (s) => resolvedPlan[s] > 0,
              )
                .map((s) => `${resolvedPlan[s]} ${s}`)
                .join(" · "),
            })}
          </Typography>
          {activeTotal === 0 && resolvedTotal > 0 && (
            <Typography
              sx={{ fontSize: "0.78rem", color: "error.main", mt: 0.5 }}
            >
              {t("evidenceNoActiveWarning")}
            </Typography>
          )}
          {resolvedTotal === 0 && (
            <Typography
              sx={{ fontSize: "0.78rem", color: "error.main", mt: 0.5 }}
            >
              {t("evidenceZeroSeedWarning", {
                fallback: SSTV2_EVIDENCE_DEFAULT_RESERVATION_COUNT,
              })}
            </Typography>
          )}
          <Typography
            sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.5 }}
          >
            {t("evidenceDoorBoundHint")}
          </Typography>
        </Box>
      )}
    </div>
  )
}

export default Sstv2EvidenceRunControls
